const text = value => String(value ?? "").trim();
const id = value => text(value).toLowerCase();
const profile = row => ({
  ...row,
  ...row?.disciplineProfile,
  ...(typeof row?.isDistinguished === "boolean" ? { isDistinguished: row.isDistinguished } : {}),
});

// Join only explicit match identities, never names or contact details.
export function buildDistinguishedReport({ registrations = [], participants = [], entries = [], scores = [], family = "bullseye" }) {
  const registrationsById = new Map(registrations.map(row => [id(row.registrationId), row]));
  const participantsById = new Map(participants.map(row => [id(row.participantId), row]));
  const entriesById = new Map(entries.map(row => [id(row.entryId), row]));
  const seen = new Set(), rows = [], errors = [];
  for (const score of scores) {
    if (score.countsForLeaderboard === false) continue;
    const name = text(score.competitorName || score.displayName) || "Unnamed competitor";
    if (family === "bullseye" && !Number(score.shotsFired) && !Number(score.totalScore) && !Number(score.xCount)) continue;
    if (family === "arc" && /^(pending|unscored|registered)$/.test(score.status || "")) continue;
    const entry = entriesById.get(id(score.canonicalEntryId || score.entryId));
    let participant = participantsById.get(id(score.participantId || entry?.participantId));
    if (!participant && !score.participantId && !entry) {
      const linked = participants.filter(row => id(row.legacyCompetitorId) && id(row.legacyCompetitorId) === id(score.competitorId));
      if (linked.length === 1) participant = linked[0];
    }
    const key = id(participant?.participantId);
    if (!key || seen.has(key)) { errors.push(`${name}: missing or duplicate roster identity.`); continue; }
    seen.add(key);
    const person = { ...profile(registrationsById.get(id(participant.registrationId))), ...profile(participant) };
    const total = family === "bullseye" ? score.totalScore : score.finalScore ?? score.totalTime ?? score.totalScore;
    const xs = family === "bullseye" ? score.xCount : null;
    if (total == null || text(total) === "" || !Number.isFinite(Number(total)) || Number(total) < 0 ||
        (family === "bullseye" && (!Number.isInteger(Number(total)) || xs == null || !Number.isInteger(Number(xs)) || Number(xs) < 0 || Number(xs) * 10 > Number(total)))) {
      errors.push(`${name}: score is missing or invalid.`); continue;
    }
    rows.push({ key, name: text(person.displayName) || name,
      distinguished: person.isDistinguished === true ? "Y" : "",
      nraId: text(person.nraMemberId || person.membershipSnapshot?.find(item => id(item.system) === "nra")?.memberId || person.bullseyeNraCompetitorId),
      city: text(person.city), state: text(person.state), totalScore: Number(total), xCount: xs == null ? null : Number(xs) });
  }
  rows.sort((a, b) => (family === "arc" ? a.totalScore - b.totalScore : b.totalScore - a.totalScore || b.xCount - a.xCount)
    || a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
  return { rows, errors };
}

export const distinguishedColumns = ["No.", "DIST", "NRA ID", "NAME", "CITY", "ST", "SCORE"];
export const distinguishedCells = (row, index) => [index + 1, row.distinguished, row.nraId, row.name, row.city, row.state,
  row.xCount === null ? `${row.totalScore.toFixed(2)}s` : `${row.totalScore}-${row.xCount}X`];
